import groupBy from 'lodash/groupBy';
import { categoricalColors20 } from 'visyn_core/utils';
import { VIS_NEUTRAL_COLOR } from 'visyn_core/vis';

import { UNKNOWN_LABEL } from '../constants/constants';
import type { Bin, CellResponse, PairedBinSeries } from '../generated/generatedOpenAPI';
import { FamilyOptions } from '../types/experiment';
import type { FamilyColors, FamilyType } from '../types/experiment';
import { toPercentages } from '../utils';

export function getTitleForFamilyGraph(familyOption: FamilyOptions): string {
  let title = 'Kappa Chain';

  if (familyOption === FamilyOptions.VH) {
    title = 'Heavy Chain';
  } else if (familyOption === FamilyOptions.VL) {
    title = 'Lambda Chain';
  }
  // else familyOption === FamilyOptions.VK

  return title;
}

export function getDataForFamilyGraph(geneData: FamilyType, familyOption: FamilyOptions, barColors: FamilyColors, asPercentage: boolean) {
  let familyData = Object.entries(geneData.kFamily);
  let barColor = barColors.VK;

  if (familyOption === FamilyOptions.VH) {
    familyData = Object.entries(geneData.hFamily);
    barColor = barColors.VH;
  } else if (familyOption === FamilyOptions.VL) {
    familyData = Object.entries(geneData.lFamily);
    barColor = barColors.VL;
  }
  // else familyOption === FamilyOptions.VK

  familyData.sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));

  const xData = familyData.map(([key]) => key);
  let yData: number[] = familyData.map(([, value]) => value);
  if (asPercentage) {
    yData = toPercentages(yData);
  }
  const data = [
    {
      type: 'bar',
      x: xData,
      y: yData,
      marker: {
        color: barColor,
      },
    },
  ];

  return data;
}

export function getTraceForClonotypeCategory(clonotypeName: string, data: CellResponse[], idx: number) {
  const tracerArray = [];
  const dataFiltered = data.filter((item: CellResponse) => item.irVdj1DuplicateCount !== null && item.irVj1DuplicateCount !== null);

  const dataX = dataFiltered.map((item: CellResponse) => {
    return item.irVdj1DuplicateCount;
  });
  const dataY = dataFiltered.map((item: CellResponse) => {
    return item.irVj1DuplicateCount;
  });
  const barCodeHoverData = dataFiltered.map((item: CellResponse) => {
    return `${item.barcode}`;
  });
  const trace: any = {
    x: dataX,
    y: dataY,
    name: clonotypeName,
    type: 'scatter',
    mode: 'markers',
    hovertext: barCodeHoverData,
    hoverinfo: 'text',
    marker: {
      color: clonotypeName === UNKNOWN_LABEL ? VIS_NEUTRAL_COLOR : categoricalColors20[idx],
    },
  };
  tracerArray.push(trace);

  return trace;
}

export const getScatterCategoryData = (scatterValues: CellResponse[], groupField: keyof CellResponse) => {
  const groupedData = groupBy(
    scatterValues.filter((item) => item[groupField]),
    groupField,
  );

  const scatterTracesData = [];

  Object.entries(groupedData).forEach(([key, value], idx) => {
    if (key === 'null') {
      return;
    }

    const dataX = (value ?? []).map((item) => item.irVdj1DuplicateCount);
    const dataY = (value ?? []).map((item) => item.irVj1DuplicateCount);
    const barCodeHoverData = (value ?? []).map((item) => item.barcode);

    const trace = {
      x: dataX,
      y: dataY,
      name: key,
      type: 'scatter',
      mode: 'markers',
      hovertext: barCodeHoverData,
      hoverinfo: 'text',
      marker: {
        color: key === UNKNOWN_LABEL ? VIS_NEUTRAL_COLOR : categoricalColors20[idx], // You can customize the color based on the key
      },
    };

    scatterTracesData.push(trace);
  });

  return scatterTracesData.sort((a, b) => a.name.localeCompare(b.name));
};

// Matches the clone size buckets computed by the backend in add_clonotype_size_category
export const CLONE_SIZE_CATEGORY_ORDER = ['1', '2-4', '5-10', '11-19', '20+', UNKNOWN_LABEL];

// Plotly violins estimate their density from raw samples and don't accept weights, so the bins are expanded back into one value per cell.
const expandBinsToSamples = (category: string, bins: Bin[], x: string[], y: number[]) => {
  for (const bin of bins) {
    for (let i = 0; i < bin.count; i++) {
      x.push(category);
      y.push(bin.value);
    }
  }
};

export const getShmViolinTracesFromSeries = (series: PairedBinSeries[], categoryOrder?: string[]) => {
  const xHeavy: string[] = [];
  const yHeavy: number[] = [];
  const xLight: string[] = [];
  const yLight: number[] = [];
  for (const item of series) {
    expandBinsToSamples(item.category, item.heavy, xHeavy, yHeavy);
    expandBinsToSamples(item.category, item.light ?? [], xLight, yLight);
  }

  const categoryValues = series
    .map((item) => item.category)
    .sort((a, b) => {
      if (categoryOrder) {
        return categoryOrder.indexOf(a) - categoryOrder.indexOf(b);
      }
      if (a === UNKNOWN_LABEL || b === UNKNOWN_LABEL) {
        return Number(a === UNKNOWN_LABEL) - Number(b === UNKNOWN_LABEL);
      }
      return a.localeCompare(b, undefined, { numeric: true });
    });

  const data = [
    {
      type: 'violin',
      points: false,
      x: xHeavy,
      y: yHeavy,
      legendgroup: 'Heavy',
      scalegroup: 'Heavy',
      name: 'Heavy',
      side: 'negative',
      box: {
        visible: false,
      },
      line: {
        color: '#337AB7',
        width: 1,
      },
      meanline: {
        visible: false,
      },
      bandwidth: 1,
    },
    {
      type: 'violin',
      points: false,
      x: xLight,
      y: yLight,
      legendgroup: 'Light',
      scalegroup: 'Light',
      name: 'Light',
      side: 'positive',
      box: {
        visible: false,
      },
      line: {
        color: '#15a154',
        width: 1,
      },
      meanline: {
        visible: false,
      },
      bandwidth: 1,
    },
  ];

  return { data, categoryValues };
};

export const getCdr3LengthTracesFromSeries = (series: PairedBinSeries[], categoryOrder?: string[]) => {
  const sortedSeries = categoryOrder
    ? [...series].sort((a, b) => categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)).reverse()
    : [...series].sort((a, b) => b.category.localeCompare(a.category, undefined, { numeric: true }));

  return sortedSeries.map((item, idx) => ({
    x: item.heavy.map((bin) => bin.value),
    y: item.heavy.map((bin) => bin.count),
    name: item.category,
    type: 'bar',
    width: 0.9,
    marker: {
      color: item.category === UNKNOWN_LABEL ? VIS_NEUTRAL_COLOR : categoricalColors20[idx % categoricalColors20.length],
    },
  }));
};
